import { useCallback, useEffect, useRef, useState } from 'react';
import '../backlog/backlog.css';
import '../accounting/accounting.css'; // .acc-tabs (the boxed child-grid tab, as Department)
import '../modules/SettingsDetailPage.css'; // the shared full-page `.sd-*` detail frame
import '../settings/systemManagement/viewLock.css'; // `.sm-viewlock` — View mode locks every field
import './reagentprep.css';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { FormReportsMenu } from '../../components/FormReportsMenu';
import { LookupCombo } from '../../components/LookupCombo';
import { SpinInput } from '../../components/SpinInput';
import { ModuleCrudDialog } from '../modules/ModuleCrudDialog';
import { MODULE_CRUD } from '../modules/moduleCrud';
import { AiCombo } from '../../components/AiCombo';
import { AuditTrailButton } from '../../components/AuditTrailButton';
import { UsDateInput } from '../../components/UsDateInput';
import { useAutoFocusFirstControl } from '../../hooks/useAutoFocusFirstControl';
import { ViewToggle } from '../../components/ViewToggle';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { api } from '../../api/client';
import { showDialog, useDeleteFlow, useSaveFlow } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { runLoading } from '../../components/loading';
import { fmtLocalDate } from '../../utils/datetime';
import {
  createReagentPrep, deleteReagentPreps, fetchComponentCoa, fetchComponents, fetchLookup,
  calculateLevel, fetchNewPrepDefaults, fetchReagentPrep, generateLevels, saveComponents, saveLevels, updateReagentPrep,
  type CalibrationLevelDto, type PrepNotepadComponentDto,
} from './api';
import { CalibrationLevelsGrid } from './CalibrationLevelsGrid';
import { guardedExit, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { useNavActions } from '../../hooks/useNavActions';
import { CALIBRATION, CHEMICAL, MICRO, prepForm, type PrepField } from './prepForms';
import { LOOKUP_GEOMETRY } from './npFields';
import { chemColumnsWithCoa, MICRO_COLS } from './componentColumns';
import {
  NA_OPTION, SOLVENT_MODES, STOCK_SOLUTIONS,
  type ReagentLookupOption, type ReagentPrepComponent, type ReagentPrepDetail,
  type ReagentPrepInput,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation — the DETAIL view: all three legacy header forms
//   ReagentPreparation_DetailView_Chemistry / _MicroMedia / _Calibration
// rendered from the per-category definitions in prepForms.ts, which are
// transcribed from the Model.xafml LAYOUTS. The record's own prepSelectType
// picks the variant, exactly as EditAction_Executing (:767-790) picks which
// DetailView to create.
//
// NOTHING IS RENDERED THAT XAF DOES NOT RENDER. The field list, its column, its
// order, its caption and its read-only state all come from prepForms.ts; this
// component only knows how to draw a PrepField. That is what stops the three
// categories drifting apart, and what stops an extra control creeping in.
//
// ── Action bar (XAF TargetViewId map, ctrl :66-79 + the DetailViews' own
//    HiddenActions) ─────────────────────────────────────────────────────────
//   Save · Save and Close · Cancel   — all three
//   Prep Notepad                     — Chemistry + Micro Media only (:69)
//   New · Delete                     — all three
//   Save and New                     — HIDDEN: every one of the three
//                                      DetailViews declares
//                                      <ActionLink ActionId="SaveAndNew"/> under
//                                      <HiddenActions>. This is why the page
//                                      does not use rule 12's Save trio.
//   Ok                               — Calibration only, and it is an
//                                      ActionContainerViewItem INSIDE the
//                                      layout beside No Of Levels, NOT a
//                                      toolbar button.
//   Reports ▾                        — Chemistry + Calibration (the views
//                                      FormViews registers); the lab-assigned
//                                      menu that replaces the hidden Prep Report /
//                                      Preview Label buttons (:71/:73).
//
// Rendered top and bottom, but NOT identical (owner request 2026-10-16): the TOP
// bar carries the full set; the BOTTOM bar only Save · Save and Close · Cancel.
// New · Delete · Reports · Prep Notepad are top-only, in that order.
//
// ⚠️ RULE 12 DEVIATION, deliberate and flagged: bespoke rather than the shared
// <SettingsDetailPage>, because that component has no slot for the
// form-specific Prep Notepad action or the inline Ok, and because XAF hides
// Save & New. Same precedent as ADR-0015's SdmsSequenceSetupPage. It is built on
// the shared full-page detail frame (.sd-wrap / .sd-head / .sd-actionbar, laid out
// as the Department detail — 2026-11-03) and composes AppDataGrid / useSaveFlow /
// useDeleteFlow / useHotkeys / useNotify, so nothing here is a second design system.
// ─────────────────────────────────────────────────────────────────────────────

function blankPrep(prepSelectType: number): ReagentPrepDetail {
  return {
    oid: '', llt: null, prepSelectType,
    prepTypeId: null, prepTypeName: null, prepNameId: null, prepNameText: null,
    testIds: [], testText: null, departmentId: null, departmentName: null,
    divisionId: null, divisionName: null, expirationDate: null,
    preparedById: null, preparedByName: null, datePrepared: null, setupDate: null,
    solvent: 0, boolSolvent: false, solventID: null,
    storageId: null, storageName: null, comment: null,
    dateDepleted: null, depletedById: null,
    // Calibration is created with SelectStockSolution = VendorStock (spec §8).
    selectStockSolution: prepSelectType === CALIBRATION ? 0 : null,
    stockConc: null, stockConcUnitId: null, stockConcUnitName: null,
    vendorStockId: null, vendorStockName: null, ltId: null, ltText: null,
    labStockId: null, labStockName: null, lltLabStockId: null, lltLabStockText: null,
    calibrationSolventId: null, calibrationSolventName: null,
    calibrationSolventLtId: null, calibrationSolventLtText: null,
    // G5 opens a new Calibration with No Of Levels = 0 (owner screenshot 2026-11-03).
    noOfLevels: prepSelectType === CALIBRATION ? 0 : null,
  };
}

/** `refId`: a related Oid the row carries but does not show (a standard
 *  name's Prep Type). */
interface GridRow { id: string; cells: string[]; refId?: string | null }

interface Props {
  id: string | null;
  newPrepType?: number;
  /** Components entered in the Prep Notepad BEFORE this record existed (the
   *  Classify → New Prep → Chemical/Micro path). Written immediately after the
   *  first Save mints the header, because the Notepad runs with no parent. */
  pendingComponents?: PrepNotepadComponentDto[] | null;
  /** The same buffer shaped as grid rows, shown until the first Save writes it. */
  pendingRows?: ReagentPrepComponent[] | null;
  /** Bumped by the list page after the Notepad's OK writes this prep's
   *  components — the grid re-reads them without remounting the page. */
  componentsVersion?: number;
  onComponentsFlushed?: () => void;
  onClose: () => void;
  onNew: () => void;
  /** Chemistry / Micro Media only — opens the Prep Notepad for this record
   *  (`viewOnly` while the form is in View mode: the Notepad only browses). */
  onPrepNotepad: (prep: ReagentPrepDetail, startComponentId?: number | null, viewOnly?: boolean) => void;
  onCreated: (id: string) => void;
  onChanged: () => void;
  /** Prev / next record arrows (BO UI Standard §4) — the current page's ids. */
  siblingIds?: string[];
  onNavigate?: (id: string, mode: 'view' | 'edit') => void;
  /** 'view' = opened by a row click: read-only until Edit (Department parity, 2026-11-03).
   *  A NEW record always opens editable. */
  initialMode?: 'view' | 'edit';
  /** The breadcrumb's Home link. */
  onHome?: () => void;
}

/** The level cells whose edit recalculates Formula + FinalConc (XAF :4226). */
/** The audit form names a preparation's history is written under (§14b.2 registry). */
const AUDIT_FORM_NAMES = ['Reagent Prep Log', 'LLT Depletion'] as const;

const LEVEL_CALC_KEYS = ['cal_Weight_g_w1', 'cal_VolumeTaken_V1', 'cal_FinalVol_V2'] as const;

/** A level Ok drew on screen that is not stored yet. */
function isPendingLevel(c: ReagentPrepComponent): boolean {
  return String(c.oid).startsWith('new-level-');
}

export default function ReagentPrepDetailPage({
  id, newPrepType = CHEMICAL, pendingComponents = null, pendingRows = null, componentsVersion = 0, onComponentsFlushed,
  onClose, onNew, onPrepNotepad, onCreated, onChanged,
  siblingIds = [], onNavigate, initialMode = 'edit', onHome,
}: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();

  const [prep, setPrep]   = useState<ReagentPrepDetail>(() => blankPrep(newPrepType));
  const [comps, setComps] = useState<ReagentPrepComponent[]>([]);
  // Calibration levels grid: XAF batch edit, so typed values are BUFFERED here
  // and stored with the header's Save — never on their own.
  const [levelEdits, setLevelEdits] = useState<Record<string, Partial<ReagentPrepComponent>>>({});
  /** Ticked rows on the Chemistry / Micro components grid (G5's selection column). */
  const [compSel, setCompSel] = useState<Set<string | number>>(new Set());
  const levelsDirty = Object.keys(levelEdits).length > 0;
  // The shared tracker cannot see a grid buffer, so register it (standing rule 15).
  // …and so is a NEW prep's Prep Notepad buffer (components entered before the
  // first Save): Cancel / leaving then asks "Unsaved changes will be lost" instead
  // of dropping them silently (owner bug list #2, 2026-11-03).
  useUnsavedGuard(levelsDirty || comps.some(isPendingLevel)
    || (id === null && (pendingComponents?.length ?? 0) > 0));
  const [loading, setLoading] = useState(id !== null);
  const [lookups, setLookups] = useState<Record<string, ReagentLookupOption[]>>({});
  // Grid-lookup feeds, keyed by "<lookup>|<parentId>" so a dependent picker
  // (LT# under Vendor Stock, Solvent LT# under Solvent) caches per parent
  // instead of clobbering its sibling.
  const [grids, setGrids] = useState<Record<string, GridRow[]>>({});
  // G5's ✎ on the Prep Name / Lab Stock grid-lookups: the Standard Name being edited.
  const [editStandardNameId, setEditStandardNameId] = useState<string | null>(null);

  const type  = prep.prepSelectType ?? newPrepType;
  const form  = prepForm(type);
  const isNew = id === null;
  // MAC rights on the Reagent Prep Log: Save needs Create (new) or Write (saved); New / Delete / Prep Notepad /
  // Ok are hidden — not greyed — for a role without the matching right. The API refuses them too (403).
  const perm = useNavActions('rgp-preplog');
  // The ✎ on Prep Name / Lab Stock opens the Standard Name record with the user's own
  // Standard Name rights (G5: one detail view, gated by its own class permission) —
  // without Write it opens read-only and cannot be promoted. The API checks the same.
  const stdPerm = useNavActions('set-standard-name');
  // View → Edit: a record opened by a row click is read-only until Edit is pressed.
  const [mode, setMode] = useState<'view' | 'edit'>(() => (initialMode === 'edit' && perm.write ? 'edit' : 'view'));
  const viewOnly = !isNew && mode === 'view';
  const canSave = !viewOnly && (isNew ? perm.create : perm.write);

  const load = useCallback(async () => {
    if (id === null) return;
    setLoading(true);
    try {
      const [header, components] = await Promise.all([fetchReagentPrep(id), fetchComponents(id)]);
      setPrep(header);
      setComps(components);
      setLevelEdits({});
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load the preparation.');
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => { void load(); }, [load]);

  // A NEW prep opens with Date Prepared = the server's current date and time,
  // as XAF's AfterConstruction does; a date the analyst already picked stays.
  useEffect(() => {
    if (id !== null) return;
    let live = true;
    fetchNewPrepDefaults()
      .then((d) => {
        // Read outside the updater: React runs it during render, where a throw blanks the page.
        const { datePrepared, preparedById, preparedByName, departmentId, departmentName } = d;
        if (live) setPrep((cur) => ({
          ...cur,
          datePrepared: cur.datePrepared ?? datePrepared,
          preparedById: cur.preparedById ?? preparedById,
          preparedByName: cur.preparedByName ?? preparedByName,
          ...(cur.departmentId ? {} : { departmentId, departmentName }),
        }));
      })
      .catch((e) => notify.error(e instanceof Error ? e.message : 'Failed to read the server date.'));
    return () => { live = false; };
  }, [id, notify]);

  // After the Notepad's OK wrote this prep's components, re-read ONLY them —
  // the header (and any unsaved edits to it) stays as it is.
  useEffect(() => {
    if (componentsVersion === 0 || id === null) return;
    let live = true;
    fetchComponents(id)
      .then((c) => { if (live) setComps(c); })
      .catch((e) => notify.error(e instanceof Error ? e.message : 'Failed to reload the components.'));
    return () => { live = false; };
  }, [componentsVersion, id, notify]);

  // Fetch exactly the lookups THIS category's form declares — no more. A
  // Chemistry form never loads the Calibration stock lists, and vice versa.
  useEffect(() => {
    const kinds = [...new Set([...form.col1, ...form.col2]
      .flatMap((f) => f.lookup && f.kind !== 'grid' && f.kind !== 'tests' ? [f.lookup] : []))];
    void (async () => {
      try {
        const loaded = await Promise.all(kinds.map(async (k) => [k, await fetchLookup(k)] as const));
        setLookups(Object.fromEntries(loaded));
      } catch (e) {
        notify.error(e instanceof Error ? e.message : 'Failed to load the dropdown lists.');
      }
    })();
  }, [form, notify]);

  /** Fetch one grid-lookup. A dependent one with no parent returns empty,
   *  which is legacy behaviour (BackendLogic §0.4 "parent empty → SelectNothing"),
   *  so that case is silent; anything else is surfaced rather than swallowed. */
  /** Returns the rows it stored, so a caller (the ✎ refresh) can use them at once. */
  const loadGrid = useCallback(async (key: string, kind: string, parentId?: string): Promise<GridRow[]> => {
    try {
      const res = await api.get<{ data?: Array<{ id: string; name: string; cols?: string[]; refId?: string | null }> }>(
        `/api/v1/reagent-preps/grid-lookups/${kind}`,
        { params: parentId ? { parentId } : undefined },
      );
      const rows = res.data?.data;
      if (!Array.isArray(rows)) {
        throw new Error(`Lookup "${kind}" did not return data. The API may be out of date — restart it.`);
      }
      const mapped = rows.map((o) => ({ id: o.id, cells: [o.name, ...(o.cols ?? [])], refId: o.refId ?? null }));
      setGrids((g) => ({ ...g, [key]: mapped }));
      return mapped;
    } catch (e) {
      setGrids((g) => ({ ...g, [key]: [] }));
      notify.error(e instanceof Error ? e.message : `Failed to load the ${kind} list.`);
      return [];
    }
  }, [notify]);

  // Load every grid-lookup this category declares, re-fetching a dependent one
  // whenever its parent changes.
  useEffect(() => {
    for (const fld of [...form.col1, ...form.col2]) {
      if (fld.kind !== 'grid' && fld.kind !== 'tests') continue;
      const parent = fld.parentKey
        ? ((prep as unknown as Record<string, unknown>)[fld.parentKey] as string | null) ?? undefined
        : undefined;
      if (fld.parentKey && !parent) { setGrids((g) => ({ ...g, [`${fld.lookup}|`]: [] })); continue; }
      void loadGrid(`${fld.lookup}|${parent ?? ""}`, fld.lookup!, parent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, loadGrid, prep.vendorStockId, prep.labStockId, prep.calibrationSolventId]);

  function patch(p: Partial<ReagentPrepDetail>) {
    setPrep((cur) => ({ ...cur, ...p }));
    // The Department follows the chosen stock item (owner request 2026-10-26).
    // The API owns the rule and the Save applies the same one; this only shows it.
    if (type === CALIBRATION && ('vendorStockId' in p || 'labStockId' in p)) {
      const vendorStockId = 'vendorStockId' in p ? p.vendorStockId : prep.vendorStockId;
      const labStockId    = 'labStockId' in p ? p.labStockId : prep.labStockId;
      fetchNewPrepDefaults({ vendorStockId, labStockId })
        .then(({ departmentId, departmentName }) => {
          if (departmentId) setPrep((cur) => ({ ...cur, departmentId, departmentName }));
        })
        .catch((e) => notify.error(e instanceof Error ? e.message : 'Failed to read the department.'));
    }
    // XAF ObjectChanged "StockConc" (:1592): a changed Stock Conc is written into
    // EVERY level's WSconc at once — value only, the unit is not touched.
    if ('stockConc' in p && type === CALIBRATION && comps.length > 0) {
      const v = p.stockConc == null || (p.stockConc as unknown) === '' ? null : String(p.stockConc);
      editLevels(comps.map((c) => c.oid), { cal_Weight_g_w1: v });
    }
  }

  /** Solvent = N/A clears SolventID and unticks BoolSolvent (spec §13).
   *  Mirrored for immediate feedback; the SERVER is authoritative and re-derives
   *  BoolSolvent on every save, so the two cannot disagree. */
  function setSolvent(v: number) {
    patch(v === 0 ? { solvent: 0, boolSolvent: false, solventID: null }
                  : { solvent: v, boolSolvent: true });
  }

  /** Calibration: the radio switches WHICH stock pair is visible, and clears
   *  the other one so a hidden field can never be saved (spec §12.1). */
  function setStockSolution(v: number) {
    patch(v === 0
      ? { selectStockSolution: 0, labStockId: null, lltLabStockId: null }
      : { selectStockSolution: 1, vendorStockId: null, ltId: null });
  }

  function toInput(): ReagentPrepInput {
    const cal = type === CALIBRATION;
    return {
      prepSelectType: type,
      prepTypeId: prep.prepTypeId, prepNameId: prep.prepNameId,
      // Calibration's form has no Test field, so it never sends one.
      testIds: cal ? null : prep.testIds,
      expirationDate: prep.expirationDate, datePrepared: prep.datePrepared,
      // ...and no enum Solvent / SolventID either.
      solvent: cal ? null : prep.solvent,
      solventID: cal ? null : prep.solventID,
      storageId: prep.storageId, comment: prep.comment,
      departmentId: prep.departmentId,
      selectStockSolution: cal ? prep.selectStockSolution : null,
      stockConc: cal ? prep.stockConc : null,
      stockConcUnitId: cal ? prep.stockConcUnitId : null,
      vendorStockId: cal ? prep.vendorStockId : null,
      ltId: cal ? prep.ltId : null,
      labStockId: cal ? prep.labStockId : null,
      lltLabStockId: cal ? prep.lltLabStockId : null,
      calibrationSolventId: cal ? prep.calibrationSolventId : null,
      calibrationSolventLtId: cal ? prep.calibrationSolventLtId : null,
      noOfLevels: cal ? prep.noOfLevels : null,
    };
  }

  async function save(close: boolean) {
    // Spec §12.1 / §18 message 8, verbatim — Calibration refuses to save with
    // no levels. The server-side required-field rules are the authority (§14b.5).
    // Required fields BEFORE the "Do you want to Save?" confirm, and ALL of them at
    // once in the form's order, as XAF's validation does (owner bug list #3,
    // 2026-11-03). The server applies the same rule and stays the authority.
    const missing = requiredMissing();
    if (missing.length > 0) {
      void showDialog({
        type: 'error',
        title: 'Data Validation Error',
        message: (
          <ul className="rgp-missing">
            {missing.map((l) => <li key={l}>{l} must not be empty.</li>)}
          </ul>
        ),
      });
      return;
    }
    if (type === CALIBRATION && comps.length === 0) {
      notify.warning('Entered the no of level and click the save.');
      return;
    }
    let newId: string | null = null;
    await runSave({
      formName: 'Reagent Prep Log',
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        const res = isNew ? await createReagentPrep(toInput())
                          : await updateReagentPrep(id!, toInput());
        newId = res.oid;
        patch({ llt: res.llt, oid: res.oid });
        // Levels after the header: the header save may push a CHANGED Stock
        // Conc into every level, and a level the analyst typed into in the
        // same Save must keep their value (XAF order of events).
        // Levels Ok drew on screen but not yet stored (XAF adds them to the
        // unsaved object, :5936). A new prep gets them from the API's create; an
        // existing one from the same GenerateLevels SP, now that the header
        // holds the No Of Levels / Stock Conc on screen.
        const pendingLevels = comps.some((c) => isPendingLevel(c));
        if (!isNew && type === CALIBRATION && pendingLevels) await generateLevels(res.oid);
        if (!isNew && type === CALIBRATION && levelsDirty && !pendingLevels) {
          await saveLevels(res.oid, levelDtos());
          setComps(await fetchComponents(res.oid));
          setLevelEdits({});
        }
        // A new Calibration: the API generated the levels with the header. Any
        // edit made to the on-screen levels is written onto them by Component ID.
        if ((isNew || pendingLevels) && type === CALIBRATION) {
          let fresh = await fetchComponents(res.oid);
          // Never drop levels the analyst drew: if the server came back with
          // fewer (an API build that did not generate them on create), generate
          // them now. The SP only appends, so a second call is harmless.
          if (fresh.length < comps.length) {
            await generateLevels(res.oid);
            fresh = await fetchComponents(res.oid);
          }
          const byNo = new Map(comps.map((c) => [Number(c.componentID), levelEdits[c.oid]]));
          const edited = fresh.flatMap((f) => {
            const e = byNo.get(Number(f.componentID));
            return e ? [{ ...f, ...e, oid: f.oid }] : [];
          });
          if (edited.length > 0) await saveLevels(res.oid, levelDtosOf(edited));
          setComps(edited.length > 0 ? await fetchComponents(res.oid) : fresh);
          setLevelEdits({});
        }
        // The Notepad ran before this record existed, so its buffer lands now —
        // one write, immediately after the header is minted.
        if (isNew && pendingComponents && pendingComponents.length > 0) {
          await saveComponents(res.oid, pendingComponents);
          setComps(await fetchComponents(res.oid));
          onComponentsFlushed?.();
        }
      },
      onSaved: () => {
        onChanged();
        if (close) onClose();
        else if (isNew && newId) onCreated(newId);
      },
    });
  }

  /** The buffered level edits, as the API takes them — every edited level is
   *  sent whole, so the server sees its complete new state. */
  function levelDtos(): CalibrationLevelDto[] {
    return levelDtosOf(comps.filter((c) => levelEdits[c.oid]).map((c) => ({ ...c, ...levelEdits[c.oid] })));
  }

  function levelDtosOf(rows: ReagentPrepComponent[]): CalibrationLevelDto[] {
    return rows.map((r) => {
      return {
        oid: r.oid,
        workingStdName: r.workingStdName, stockStdName: r.stockStdName,
        cal_Weight_g_w1: r.cal_Weight_g_w1,       calWsUnitsId: r.calWsUnitsId ?? null,
        cal_VolumeTaken_V1: r.cal_VolumeTaken_V1, calV1UnitsId: r.calV1UnitsId ?? null,
        cal_FinalVol_V2: r.cal_FinalVol_V2,       calV2UnitsId: r.calV2UnitsId ?? null,
        cal_FinalConc_C2: r.cal_FinalConc_C2,     calC2UnitsId: r.calC2UnitsId ?? null,
        formula: r.formula, comment: r.comment,
      };
    });
  }

  /** Per-level request counter, so a slow answer never overwrites a newer one. */
  const calcSeq = useRef<Record<string, number>>({});

  /**
   * A grid edit. Editing WSconc, VolTaken or FinalVol recalculates that level's
   * Formula + FinalConc at once, as XAF's BatchEditEndEditing script does
   * (ctrl :4226) — through the server rule the Save applies, never in the page.
   */
  function onLevelEdit(oids: string[], p: Partial<ReagentPrepComponent>) {
    editLevels(oids, p);
    if (!LEVEL_CALC_KEYS.some((k) => k in p)) return;
    for (const oid of oids) {
      const base = comps.find((c) => c.oid === oid);
      if (!base) continue;
      const row = { ...base, ...levelEdits[oid], ...p };
      const seq = (calcSeq.current[oid] ?? 0) + 1;
      calcSeq.current[oid] = seq;
      calculateLevel({
        cal_Weight_g_w1: row.cal_Weight_g_w1 ?? null,
        cal_VolumeTaken_V1: row.cal_VolumeTaken_V1 ?? null,
        cal_FinalVol_V2: row.cal_FinalVol_V2 ?? null,
        cal_FinalConc_C2: row.cal_FinalConc_C2 ?? null,
      }).then((r) => {
        if (calcSeq.current[oid] !== seq) return;
        editLevels([oid], { formula: r.formula, cal_FinalConc_C2: r.cal_FinalConc_C2 });
      }).catch((e) => notify.error(e instanceof Error ? e.message : 'Failed to calculate the level.'));
    }
  }

  function editLevels(oids: string[], p: Partial<ReagentPrepComponent>) {
    setLevelEdits((cur) => {
      const next = { ...cur };
      for (const o of oids) next[o] = { ...next[o], ...p };
      return next;
    });
  }

  /** Delete through the shared flow — confirm, the reason when Audit Trail
   *  Configuration requires one, then "Deleted successfully." and back to the list. */
  async function remove() {
    if (isNew) { onClose(); return; }
    await runDelete({
      formName: 'Reagent Prep Log',
      message: `Delete preparation ${prep.llt ?? ''}? Its components are deleted with it.`,
      delete: async () => { await deleteReagentPreps([id!]); },
      onDeleted: () => { onChanged(); onClose(); },
    });
  }

  /** Calibration [Ok] — spec §12.2. */
  function makeLevels() {
    // XAF Ok_Level_Execute (:5936) works on the UNSAVED object, for a new AND a
    // saved prep: append up to the No Of Levels ON SCREEN (never remove), each
    // new level taking WSconc + unit from the Stock Conc ON SCREEN. Nothing is
    // stored until Save — the old saved-prep path called the SP at once, which
    // read the STORED No Of Levels / Stock Conc and ignored what was typed.
    {
      const want = Number(prep.noOfLevels ?? 0);
      // Owner rule 2026-10-23 (stricter than XAF, which silently does nothing):
      // Ok needs at least one level AND every mandatory header field filled, so
      // levels are never drawn for a header that cannot be saved.
      if (!Number.isInteger(want) || want < 1) {
        notify.warning('No Of Levels must be at least 1.');
        return;
      }
      const missing = requiredMissing();
      if (missing.length > 0) {
        notify.warning(`Fill the mandatory fields before clicking OK: ${missing.join(', ')}.`);
        return;
      }
      // XAF: fewer rows than No Of Levels, else Ok does nothing (never removes).
      if (want <= comps.length) return;
      const unitName = (lookups.units ?? []).find((u) => u.id === prep.stockConcUnitId)?.name ?? null;
      const added = Array.from({ length: want - comps.length }, (_, k) => {
        const n = comps.length + k + 1;
        return {
          oid: `new-level-${n}`, componentID: n,
          cal_Weight_g_w1: prep.stockConc == null ? null : String(prep.stockConc),
          calWsUnitsId: prep.stockConcUnitId ?? null, calWsUnitsName: unitName,
        } as unknown as ReagentPrepComponent;
      });
      setComps([...comps, ...added]);
    }
  }

  // ── Field renderer — draws exactly what the PrepField says, nothing more ──
  /**
   * BRANCH_C: choosing the Items row named "Water" as the Calibration Solvent
   * makes Solvent LT# read-only — water needs no lot (BackendLogic C §2.C).
   *
   * Legacy never re-enables it when the solvent changes away from Water (a
   * documented bug in that same section); this recomputes it from the current
   * value each render, so picking a different solvent makes the field editable
   * again. The name test is against the item's display text, as in legacy.
   */
  const solventIsWater =
    (prep.calibrationSolventName ?? '').trim().toLowerCase() === 'water';

  /** Captions of the required fields shown on this form that are still empty, in
   *  form order — the ONE rule behind the Save check and Calibration's Ok check. */
  function requiredMissing(): string[] {
    return [...form.col1, ...form.col2]
      .filter((f) => f.required && isFieldShown(f))
      .filter((f) => {
        const v = (prep as unknown as Record<string, unknown>)[f.key];
        return v === null || v === undefined || v === '';
      })
      .map((f) => f.label);
  }

  /** The Vendor / Lab Stock pair shows one half at a time (SelectStockSolution). */
  function isFieldShown(f: PrepField): boolean {
    if (f.showWhen === 'vendorStock') return (prep.selectStockSolution ?? 0) === 0;
    if (f.showWhen === 'labStock')    return (prep.selectStockSolution ?? 0) === 1;
    return true;
  }

  function renderField(f: PrepField) {
    if (!isFieldShown(f)) return null;
    const waterLocked = f.key === 'calibrationSolventLtId' && solventIsWater;

    const raw = (prep as unknown as Record<string, unknown>)[f.key];
    // Required-yet-empty gets the yellow tint (BO UI Standard §4 / rule 12).
    const empty = raw === null || raw === undefined || raw === '';
    // The shared .sd-input already paints EVERY editable field yellow (BO UI
    // Standard, app-wide since 2026-07-10). The modifier only adds the amber
    // BORDER a required-yet-empty field gets on top of that.
    const tint  = f.required && empty ? ' required-empty' : '';

    let control: React.ReactNode;
    switch (f.kind) {
      case 'readonly':
        control = <input type="text" className="sd-input readonly" readOnly
                         value={(raw as string | null) ?? ''} />;
        break;
      case 'text':
        control = <input type="text" className={`sd-input${tint}`} value={(raw as string | null) ?? ''}
                         // SolventID is editable only when Solvent ≠ N/A (§13).
                         readOnly={f.key === 'solventID' && !prep.boolSolvent}
                         onChange={(e) => patch({ [f.key]: e.target.value || null } as Partial<ReagentPrepDetail>)} />;
        break;
      case 'textarea':
        control = <textarea rows={f.rows ?? 3} className={`sd-input${tint}`} value={(raw as string | null) ?? ''}
                            onChange={(e) => patch({ [f.key]: e.target.value || null } as Partial<ReagentPrepDetail>)} />;
        break;
      case 'date':
      case 'datePast':
        // The shared UsDateInput: ALWAYS MM/DD/YYYY (standing rule 3), whatever the
        // browser locale — a native <input type="date"> shows the browser's format,
        // which read "24-11-2026" here (owner report 2026-11-03; G5: 11/24/26). The
        // calendar is still the browser's own.
        control = <UsDateInput className={`sd-input${tint}`}
                               // XAF disables future dates on DatePrepared (§18 msg 3).
                               max={f.kind === 'datePast' ? today() : undefined}
                               value={dateInput(raw as string | null)}
                               // The date only (XAF's default DateTime display); a new
                               // prep's server time is kept when just the day changes.
                               onChange={(day) => {
                                 const time = typeof raw === 'string' && raw.includes('T') ? raw.slice(raw.indexOf('T')) : '';
                                 patch({ [f.key]: day ? day + time : null } as Partial<ReagentPrepDetail>);
                               }} />;
        break;
      case 'number':
        control = <input type="number" step="any" className={`sd-input${tint}`} value={(raw as number | null) ?? ''}
                         onChange={(e) => patch({ [f.key]: e.target.value === '' ? null : Number(e.target.value) } as Partial<ReagentPrepDetail>)} />;
        break;
      case 'lookup':
        // The shared AiCombo: drop arrow + clear for an FK reference — the clear is
        // Sample Registration's inline ⊗ (clearInline, owner request 2026-11-03),
        // shown once there is a value. Its top "N/A" row clears to null, never text.
        control = (
          <AiCombo
            value={(raw as string | null) ?? null}
            options={(lookups[f.lookup!] ?? []).map((o) => ({ value: o.id, label: o.name ?? '' }))}
            onChange={(v) => patch({ [f.key]: v || null } as Partial<ReagentPrepDetail>)}
            disabled={waterLocked}
            placeholder={NA_OPTION.label}
            erasable
            clearInline
            ariaLabel={f.label}
            className={tint ? tint.trim() : undefined}
          />
        );
        break;
      case 'grid': {
        const parent = f.parentKey
          ? ((prep as unknown as Record<string, unknown>)[f.parentKey] as string | null) ?? ""
          : "";
        const rows = grids[`${f.lookup}|${parent}`] ?? [];
        const shown = f.textKey
          ? ((prep as unknown as Record<string, unknown>)[f.textKey] as string | null) ?? ""
          : "";
        // Rows are identified by their OWN Oid, never by the name in column 0:
        // names repeat (one standard name has a row per prep type), and keying by
        // name highlighted — and could pick — every row sharing it.
        const idOfRow = new Map(rows.map((r) => [r.cells, r.id] as const));
        const selectedId = ((prep as unknown as Record<string, unknown>)[f.key] as string | null) ?? '';
        control = (
          <LookupCombo
            lookup={{ cols: f.cols ?? ['Name'], rows: rows.map((r) => r.cells) }}
            value={selectedId}
            rowKey={(row) => idOfRow.get(row) ?? ''}
            labelText={shown}
            disabled={waterLocked}
            popupWidth={LOOKUP_GEOMETRY[f.lookup!]?.popup}
            colWidths={LOOKUP_GEOMETRY[f.lookup!]?.widths}
            wrapCells
            className={tint ? tint.trim() : undefined}
            // N/A first: clears this pick and any lookup that depends on it
            // (LT# under Vendor Stock, LLT# under Lab Stock, Solvent LT# under Solvent).
            naOption
            // No clear button on the grid lookups — they never had one in G6 (owner
            // 2026-11-03: "if not available then don't add it"); N/A still clears.
            // G5's ✎ — opens the PICKED Standard Name for editing (Prep Name, Lab
            // Stock), only while a value is picked, exactly as G5 shows it.
            // Tooltip "Edit", exactly as G5 (owner report 2026-11-03) — the shared default.
            onEdit={f.lookup === 'lab-stock' && selectedId ? () => setEditStandardNameId(selectedId) : undefined}
            // Plain text value — no grey label chip (owner request 2026-10-16).
            plainValue
            onClear={() => {
              const next: Record<string, null> = { [f.key]: null };
              if (f.textKey) next[f.textKey] = null;
              for (const dep of [...form.col1, ...form.col2]) {
                if (dep.parentKey !== f.key) continue;
                next[dep.key] = null;
                if (dep.textKey) next[dep.textKey] = null;
              }
              patch(next as Partial<ReagentPrepDetail>);
            }}
            onPick={(row) => {
              // By row IDENTITY only — never fall back to the name, which repeats.
              const found = rows.find((r) => r.cells === row) ?? null;
              const next: Partial<ReagentPrepDetail> = {
                [f.key]: found?.id ?? null,
                ...(f.textKey ? { [f.textKey]: row[0] ?? null } : {}),
              } as Partial<ReagentPrepDetail>;
              // Mutual exclusion (BackendLogic C §2.B/§2.E): choosing one
              // stock side clears the other, together with its lot.
              if (f.key === 'vendorStockId')
                Object.assign(next, { labStockId: null, labStockName: null, lltLabStockId: null, lltLabStockText: null });
              if (f.key === 'labStockId')
                Object.assign(next, { vendorStockId: null, vendorStockName: null, ltId: null, ltText: null });
              // XAF ObjectChanged :1567 — picking a Prep Name sets Prep Type to that
              // standard name's own type (only when it has one; the user may still
              // change it). Calibration has no Prep Type field; its server save
              // derives it the same way.
              if (f.key === 'prepNameId' && found?.refId)
                Object.assign(next, { prepTypeId: found.refId, prepTypeName: row[1] ?? null });
              patch(next);
            }}
          />
        );
        break;
      }
      case 'tests': {
        // XAF's checked grid-lookup: the ticked rows are the TestMethod Oids, the
        // closed combo shows "TestName | MethodNumber" (TextFormatString {2} | {3}).
        const rows = grids['tests|'] ?? [];
        const idOf = new Map(rows.map((r) => [r.cells, r.id] as const));
        const chosen = new Set(prep.testIds ?? []);
        const shown = rows.filter((r) => chosen.has(r.id))
          .map((r) => `${r.cells[1] ?? ''} | ${r.cells[2] ?? ''}`).join(';');
        control = (
          <LookupCombo
            multi
            naOption
            // Plain text value — no grey label chip (owner request 2026-10-16).
            plainValue
            lookup={{ cols: f.cols ?? ['Name'], rows: rows.map((r) => r.cells) }}
            rowKey={(row) => idOf.get(row) ?? row.join('|')}
            values={prep.testIds ?? []}
            labelText={shown || prep.testText || ''}
            popupWidth={560}
            colWidths={[150, 220, 150]}
            wrapCells
            className={tint ? tint.trim() : undefined}
            onToggle={(next) => patch({ testIds: next })}
          />
        );
        break;
      }
      case 'enum':
        // Solvent is an enum, not an FK — no eraser. Mode 0 IS "N/A", so it is the
        // combo's own top N/A row rather than a second N/A option.
        control = (
          <AiCombo
            value={prep.solvent ? String(prep.solvent) : null}
            options={SOLVENT_MODES.filter((m) => m.value !== 0).map((m) => ({ value: String(m.value), label: m.label }))}
            onChange={(v) => setSolvent(v ? Number(v) : 0)}
            placeholder="N/A"
            ariaLabel={f.label}
          />
        );
        break;
      case 'radio':
        control = (
          <div className="rgp-radiorow" role="radiogroup" aria-label={f.label}>
            {STOCK_SOLUTIONS.map((s) => (
              <label key={s.value} className="rgp-radio">
                <input type="radio" name="rgp-stock"
                       checked={(prep.selectStockSolution ?? 0) === s.value}
                       onChange={() => setStockSolution(s.value)} />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
        );
        break;
      case 'checkbox':
        // BoolSolvent: AllowEdit="False", Caption="" — read-only, no caption,
        // no added text of our own.
        control = <input type="checkbox" checked={!!prep.boolSolvent} readOnly disabled
                         aria-label="Solvent" />;
        break;
      case 'levels':
        // NoOfLevels + the inline [Ok] ActionContainerViewItem — ONE layout row,
        // exactly as Model.xafml nests them in a horizontal LayoutGroup.
        control = (
          <div className="rgp-inline">
            {/* G5's SpinEdit — always-visible ▲/▼ (owner report 2026-11-03), the
                shared stepper; whole levels from 0 (no practical upper limit). */}
            <SpinInput className="sd-input rgp-spin" min={0} max={9999} step={1} ariaLabel="No Of Levels"
                       value={prep.noOfLevels == null ? '' : String(prep.noOfLevels)}
                       onChange={(v) => patch({ noOfLevels: v === '' ? null : Math.trunc(Number(v)) })} />
            {/* Caption "OK" in the theme colour — the shared solid primary
                (.ca-btn-solid), as the dialogs' OK (owner request 2026-11-03). */}
            {canSave && (
              <button type="button" className="ca-btn ca-btn-solid" onClick={() => makeLevels()} title="OK">
                OK
              </button>
            )}
          </div>
        );
        break;
    }

    return (
      <div className={`rgp-field${f.label === '' ? ' rgp-field--nolabel' : ''}`} key={f.key}>
        {/* Shared .sd-label + .req — the same label styling as every other form. */}
        <label className="rgp-label sd-label">
          {f.label}{f.required && <span className="req" aria-hidden="true">*</span>}
        </label>
        <div className="rgp-control">{control}</div>
      </div>
    );
  }

  // ── Action bar — the shared detail frame's `.sd-actionbar` (Department parity) ──
  // TOP bar = the full set; BOTTOM bar = the Save group only (owner request
  // 2026-10-16: New · Delete · Reports · Prep Notepad belong to the top bar alone).
  // View mode (a record opened by a row click) shows Edit · Close instead of the
  // Save group, as Department does.
  const leave = () => { void guardedExit(onClose); };
  const startEdit = () => setMode('edit');
  const openNotepad = () => onPrepNotepad(prep, undefined, viewOnly);
  const showNotepad = form.hasPrepNotepad && (canSave || (viewOnly && !isNew));
  const showDelete  = perm.delete && !isNew;
  /** G5 Refresh: re-read this record — the shared "Unsaved changes will be lost" first. */
  const refresh = () => { void guardedExit(() => { void load(); }); };

  const saveGroup = (
    <div className="sd-ab-right">
      {viewOnly ? (
        <>
          {perm.write && (
            <button type="button" className="sd-abtn primary" onClick={startEdit} disabled={loading}
                    title={`Edit (${formatCombo('alt+e')})`}>
              <span className="tb-i tb-i-blue">✎</span> Edit
            </button>
          )}
          <button type="button" className="sd-abtn cancel" onClick={onClose} title={`Close (${formatCombo('esc')})`}>
            <span className="tb-i">↩︎</span> Close
          </button>
        </>
      ) : (
        <>
          {canSave && (
            <button type="button" className="sd-abtn primary" onClick={() => void save(false)} disabled={loading}
                    title={`Save (${formatCombo('mod+s')})`}>
              <span className="tb-i">💾</span> Save
            </button>
          )}
          {canSave && (
            <button type="button" className="sd-abtn" onClick={() => void save(true)} disabled={loading}
                    title={`Save and Close (${formatCombo('mod+enter')})`}>
              <span className="tb-i">💾</span> Save and Close
            </button>
          )}
          {canSave && <span className="sd-abtn-sep" aria-hidden="true" />}
          <button type="button" className="sd-abtn cancel" onClick={leave} title={`Cancel (${formatCombo('esc')})`}>
            <span className="tb-i">↩︎</span> Cancel
          </button>
        </>
      )}
    </div>
  );

  const actions = (top: boolean) => top ? (
    <div className="sd-actionbar sd-actionbar--split">
      <div className="sd-ab-left">
        {/* Audit Trail for THIS record, first on the bar as on the shared detail page.
            A prep's history is written under two form names against its Oid — header
            edits ("Reagent Prep Log") and the depletion stamp ("LLT Depletion") — so
            the log shows both. Config-gated by Settings → Audit Trail Configuration. */}
        {!isNew && id && (
          <AuditTrailButton formName="Reagent Prep Log" formNames={AUDIT_FORM_NAMES} sourceId={id} label="Audit Trail" />
        )}
        {/* G5's order (owner screenshots 2026-11-03): New · Reports · Delete · Refresh
            [· Prep Notepad]. On a NEW record (opened from the Classify prep-type pick)
            New · Reports · Delete · Refresh are NOT shown at all (owner request
            2026-11-03) — nothing to report, delete or re-read yet, and New would only
            re-open the Classify popup over an unsaved prep. Prep Notepad stays: it is
            how a new Chemistry / Micro prep gets its components. */}
        {!isNew && perm.create && (
          <button type="button" className="sd-abtn" onClick={() => void guardedExit(onNew)}
                  title={`New Reagent Preparation (${formatCombo('alt+n')})`}>
            <span className="tb-i tb-i-green">＋</span> New
          </button>
        )}
        {/* Reports ▾ for the OPEN record — legacy registers it on the Chemistry and
            Calibration detail views only (dbo.FormViews has no Micro detail view).
            With no LLT yet the shared menu is greyed. */}
        {!isNew && type !== MICRO && (
          <FormReportsMenu
            reportsUrl={`/api/v1/reagent-preps/reports?view=${type === CALIBRATION ? 'calibration' : 'chemistry'}`}
            pdfUrl="/api/v1/reagent-preps/report-pdf"
            keysParam="llts"
            // G5 keeps Reports clickable with nothing selected (bug list #9, 2026-11-03).
            enabledWithoutSelection
            selectedKeys={prep.llt ? [prep.llt] : []}
          />
        )}
        {showDelete && (
          <button type="button" className="sd-abtn danger" onClick={() => void remove()} disabled={loading}
                  title={`Delete (${formatCombo('alt+d')})`}>
            <span className="tb-i tb-i-red">🗑</span> Delete
          </button>
        )}
        {/* Refresh re-reads the record; it asks first when there are unsaved edits. */}
        {!isNew && (
          <button type="button" className="sd-abtn" onClick={refresh} disabled={loading}
                  title={`Refresh (${formatCombo('alt+r')})`}>
            <span className="tb-i tb-i-green">🔄</span> Refresh
          </button>
        )}
        {/* Prep Notepad — LAST, after Reports; top bar only (owner request 2026-10-16).
            Chemistry + Micro Media only (:69). In View mode it opens read-only. */}
        {showNotepad && (
          <button type="button" className="sd-abtn" onClick={openNotepad} title="Prep Notepad">
            <span className="tb-i">📓</span> Prep Notepad
          </button>
        )}
      </div>
      {saveGroup}
    </div>
  ) : (
    <div className="sd-actionbar bottom">{saveGroup}</div>
  );

  /** COA "Attachment" on a component row (XAF PreviewCOA_Reagent) — opens the LT
   *  vendor certificate or the LLT notebook in a new tab; nothing to show → say so. */
  async function previewCoa(row: ReagentPrepComponent) {
    if (!id) return;
    try {
      const blob = await runLoading(fetchComponentCoa(id, row.oid), { blocking: true, label: 'Opening the COA…' });
      if (!blob) { notify.info('No COA is available for this component.'); return; }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to open the COA.');
    }
  }

  const childCols: AppColumn<ReagentPrepComponent>[] =
    type === MICRO ? MICRO_COLS : chemColumnsWithCoa((row) => void previewCoa(row), !isNew);
  const levelRows = comps.map((c) => (levelEdits[c.oid] ? { ...c, ...levelEdits[c.oid] } : c));

  // Prev / next record arrows (BO UI Standard §4) — keep the current mode, ask
  // first when there are unsaved changes (standing rule 15).
  const pos  = id ? siblingIds.indexOf(id) : -1;
  const prev = pos > 0 ? siblingIds[pos - 1] : null;
  const next = pos >= 0 && pos < siblingIds.length - 1 ? siblingIds[pos + 1] : null;
  const goTo = (nid: string | null | undefined) => {
    if (nid && onNavigate) void guardedExit(() => onNavigate(nid, viewOnly ? 'view' : 'edit'));
  };

  // Keyboard shortcuts through the ONE shared hotkey layer (standing rule 14).
  const keys: HotkeyDef[] = [
    ...(perm.create && !isNew ? [{ combo: 'alt+n', label: 'New', group: 'Reagent Prep', order: 1, run: () => void guardedExit(onNew) }] : []),
    ...(showDelete ? [{ combo: 'alt+d', label: 'Delete', group: 'Reagent Prep', order: 2, disabled: loading, run: () => void remove() }] : []),
    ...(!isNew ? [{ combo: 'alt+r', label: 'Refresh', group: 'Reagent Prep', order: 2, disabled: loading, run: refresh }] : []),
    ...(viewOnly ? [
      ...(perm.write ? [
        { combo: 'alt+e', label: 'Edit', group: 'Reagent Prep', order: 3, run: startEdit },
        { combo: 'f2', label: 'Edit', group: 'Reagent Prep', order: 3, hidden: true, run: startEdit },
      ] : []),
      { combo: 'esc', label: 'Close', group: 'Reagent Prep', order: 6, allowInInput: false, run: onClose },
    ] : [
      ...(canSave ? [
        { combo: 'mod+s', label: 'Save', group: 'Reagent Prep', order: 4, disabled: loading, run: () => void save(false) },
        { combo: 'mod+enter', label: 'Save & Close', group: 'Reagent Prep', order: 5, disabled: loading, run: () => void save(true) },
      ] : []),
      { combo: 'esc', label: 'Cancel', group: 'Reagent Prep', order: 6, allowInInput: false, run: leave },
    ]),
    { combo: 'alt+left', label: 'Previous record', group: 'Reagent Prep', order: 7, disabled: !prev, run: () => goTo(prev) },
    { combo: 'alt+right', label: 'Next record', group: 'Reagent Prep', order: 8, disabled: !next, run: () => goTo(next) },
  ];
  useHotkeys(keys);

  /** The record's name in the header — its Prep Name, as G5 shows it (else its LLT). */
  const recordName = (prep.prepNameText ?? '').trim() || prep.llt || '';

  // The cursor lands in the first usable field when the form opens editable, and
  // again when Edit is pressed (standing rule 14; Department autofocuses its first box).
  const fieldsRef = useRef<HTMLFieldSetElement>(null);
  useAutoFocusFirstControl(fieldsRef, !viewOnly && !loading);

  return (
    <div className="bl-page mlv-host rgp-detail">
      {/* List / Detail switch (Department parity): "List View" goes back to the list,
          asking first when there are unsaved changes. */}
      <div className="rgp-viewtoggle">
        <ViewToggle view="detail" onChange={(v) => { if (v === 'list') leave(); }} />
      </div>
      <div className="bl-grid-area">
        <div className="sd-wrap">
          <div className="sd-head">
            <div>
              {/* As G5 (owner screenshots 2026-11-03): the title is always "Reagent Prep
                  Log", followed on a saved record by its PREP NAME ("Reagent Prep Log -
                  PFAS533_LFSMD_L"); the breadcrumb ends with that name, or with the form
                  type ("Calibration") on a new record. */}
              <h2>Reagent Prep Log{!isNew && recordName ? ` – ${recordName}` : ''}</h2>
              <div className="sd-bc">
                <a onClick={() => void guardedExit(() => onHome?.())}>Home</a><i className="ti ti-chevron-right" />
                <span>Reagent Preparation</span><i className="ti ti-chevron-right" />
                <a onClick={leave}>Reagent Prep Log</a><i className="ti ti-chevron-right" />
                {/* A new Chemistry / Micro form's caption IS "Reagent Prep Log", which
                    repeated the crumb before it — so it reads "New" (bug list #4). */}
                <span>{!isNew && recordName ? recordName
                  : form.caption === 'Reagent Prep Log' ? 'New' : form.caption}</span>
              </div>
            </div>
            {/* Always shown, greyed when there is no previous / next record (Department). */}
            {onNavigate && (
              <div className="sd-navarrows">
                <button type="button" className="sd-na" aria-label="Previous record" disabled={!prev}
                        title={`Previous Record (${formatCombo('alt+left')})`} onClick={() => goTo(prev)}>‹</button>
                <button type="button" className="sd-na next" aria-label="Next record" disabled={!next}
                        title={`Next Record (${formatCombo('alt+right')})`} onClick={() => goTo(next)}>›</button>
              </div>
            )}
          </div>

          {actions(true)}

          <div className="sd-body">
            {/* "Basic Information" — the XAF layout group caption G5 shows over the
                fields (owner report 2026-11-03), drawn with the shared detail-page
                section heading. No frame round the fields (Department). View mode
                locks every field in one go (.sm-viewlock). */}
            <div className="sd-section-h">Basic Information</div>
            <fieldset ref={fieldsRef} className="sm-viewlock rgp-fields" disabled={viewOnly}>
              <div className="rgp-cols">
                <div className="rgp-col">{form.col1.map(renderField)}</div>
                <div className="rgp-col">{form.col2.map(renderField)}</div>
              </div>
            </fieldset>

            {/* The child grid's caption as G5 shows it: only Chemistry's group carries
                one ("Components", drawn as a boxed tab like Department's Employees tab);
                Calibration and Micro Media set ShowCaption="False", so nothing is drawn
                above their grid (owner screenshot 2026-11-03 — the earlier "Levels"
                label was ours, not G5's). */}
            {form.childCaption && (
              <div className="acc-tabs rgp-tabs-boxed" role="tablist">
                <button type="button" role="tab" aria-selected="true" className="acc-tab active">
                  {form.childCaption}
                </button>
              </div>
            )}
            <div className={`rgp-tabpanel${form.childCaption ? '' : ' rgp-tabpanel--bare'}`} role={form.childCaption ? 'tabpanel' : undefined}>
              {type === CALIBRATION ? (
                <CalibrationLevelsGrid
                  prepId={id}
                  prep={prep}
                  rows={levelRows}
                  units={lookups.units ?? []}
                  loading={loading}
                  readOnly={viewOnly}
                  onEdit={onLevelEdit}
                />
              ) : (
                <AppDataGrid<ReagentPrepComponent>
                  selectAllTitle="Selection state of all rows on all pages"
                  rows={isNew && pendingRows ? pendingRows : comps}
                  columns={childCols}
                  getRowId={(r) => r.oid}
                  loading={loading}
                  emptyText="No data to display"
                  // The tick-box column G5's nested list shows (owner request 2026-11-03,
                  // match G5) — the Calibration levels grid already has it.
                  selectable
                  selectedIds={compSel}
                  onSelectionChange={setCompSel}
                  // A row click opens the Prep Notepad ON that component, its data
                  // filled — read-only while the form is in View mode.
                  onRowClick={!isNew && form.hasPrepNotepad
                    ? (r) => onPrepNotepad(prep, r.componentID == null ? null : Number(r.componentID), viewOnly)
                    : undefined}
                />
              )}
            </div>
          </div>

          {actions(false)}
        </div>
      </div>
      {/* G5 ✎ on Prep Name / Lab Stock — the Settings › Standard Name record in the
          shared CRUD popup, opened for editing. Its Save is that form's own audited
          PUT ("Standard Name"); afterwards the lookup is re-read and the picked
          row's new name shown, without touching this prep's unsaved edits. */}
      {editStandardNameId && (
        <ModuleCrudDialog
          // As G5's popup (owner request 2026-11-04): titled "{name} - Standard Name",
          // buttons OK · Cancel (the shared popup standard, dialogAccent). Overridden
          // HERE only — Settings › Standard Name keeps its own popup unchanged.
          config={{
            ...MODULE_CRUD['set-standard-name']!,
            dialogTitle: `${(grids['lab-stock|'] ?? []).find((r) => r.id === editStandardNameId)?.cells[0] ?? ''} - Standard Name`.replace(/^ - /, ''),
            dialogSaveLabel: 'OK',
            dialogAccent: true,
            dialogStandard: true,
            // 50-50 with 2 rows (owner request 2026-11-04): Standard Name above Prep Type,
            // each row split evenly between caption and box (reagentprep.css .rgp-std-popup).
            dialogFieldsClass: 'rgp-std-popup',
          }}
          id={editStandardNameId}
          mode={stdPerm.write ? 'edit' : 'view'}
          canEdit={stdPerm.write}
          elevated
          onClose={() => setEditStandardNameId(null)}
          onSaved={() => {
            const edited = editStandardNameId;
            setEditStandardNameId(null);
            void (async () => {
              const row = (await loadGrid('lab-stock|', 'lab-stock')).find((r) => r.id === edited);
              if (!row) return;
              setPrep((cur) => ({
                ...cur,
                ...(cur.prepNameId === edited ? { prepNameText: row.cells[0] ?? cur.prepNameText } : {}),
                ...(cur.labStockId === edited ? { labStockName: row.cells[0] ?? cur.labStockName } : {}),
              }));
            })();
          }}
        />
      )}
    </div>
  );
}

/** A wire date → the `yyyy-MM-dd` an <input type="date"> needs. Goes through the
 *  shared helper, so no date formatting is inlined here (C2). */
function dateInput(v: string | null): string {
  if (!v) return '';
  const [m, d, y] = fmtLocalDate(v).split('/');
  return y && m && d ? `${y}-${m}-${d}` : '';
}

function today(): string {
  const [m, d, y] = fmtLocalDate(new Date().toISOString()).split('/');
  return `${y}-${m}-${d}`;
}
